/**
 * Initializes all application keybindings related to transport, editing, zooming, grid & note resolution,
 * and piano-roll selection manipulation.
 *
 * USER KEYBINDINGS
 *
 * Transport (Global Scope):
 * - Space / (implementation dependent via buildKeybindingHandlers): Play / Pause (playPause)
 * - (Recording shortcut as defined in buildKeybindingHandlers): Start / Stop Recording (record)
 *
 * Metronome (Global Scope):
 * - Ctrl + M: Toggle metronome on/off
 *
 * Note Duration (Global Scope):
 * - [: Decrease currently selected note input duration
 * - ]: Increase currently selected note input duration
 *
 * Grid Resolution (Global Scope):
 * - {: Decrease grid resolution (coarser grid)
 * - }: Increase grid resolution (finer grid)
 *
 * Zoom (Global Scope):
 * - -: Zoom out (horizontal / timeline)
 * - +: Zoom in (horizontal / timeline)
 *
 * Edit Mode (Global UI Scope):
 * - W: Toggle between NOTE ENTRY mode ("NOTES") and SELECTION mode ("SELECT")
 *
 * Piano Roll Selection Editing (Selection-Active Scope):
 * (These only fire when there is an active selection and the selection resolver returns data.)
 * - Shift + ArrowUp: Transpose selected notes up one octave
 * - Shift + ArrowDown: Transpose selected notes down one octave
 * - Delete: Delete selected notes
 * - ArrowUp: Nudge selected notes up by one semitone
 * - ArrowDown: Nudge selected notes down by one semitone
 * - ArrowLeft: Nudge selected notes left in time (uses time signature context for quantized movement)
 * - ArrowRight: Nudge selected notes right in time (uses time signature context for quantized movement)
 * - D: Duplicate selected notes (time-aware; uses time signature + selection context)
 * - F: Fill gaps or extend selected notes (behavior defined in PianoRollEditKB.fillNotes)
 * - S: Split selected notes (NOTE: The description label in code says "Duplicate notes" but the handler is splitNotes -- likely a labeling bug.)
 *
 * Track / Wider Selection Scope:
 * - Ctrl + A: Select all notes in the current track (scopeUnit: 'track')
 *
 * SCOPES
 *
 * - global: Always active (no selection required).
 * - selection-active: Requires a non-empty current note selection; uses resolver to inject selection + timing context.
 * - track: Active when a track context is focused; enables operations like "Select All".
 *
 * EXTENSIBILITY NOTES
 *
 * - To add a new keybinding, use KeybindingsManager.getInstance().register({ ... }).
 * - Ensure you pick an appropriate scopeUnit to prevent accidental global overrides.
 * - Provide a resolver when the handler requires dynamic runtime data (e.g., current selection, playhead, timing).
 * - Keep textual description precise; it is used for UI help / debugging.
 *
 * POTENTIAL INCONSISTENCIES
 *
 * - The "S" keybinding (handler: splitNotes) currently has description "Duplicate notes". Consider renaming to "Split notes" for clarity.
 *
 * ACCESSIBILITY / UX RECOMMENDATIONS
 *
 * - Consider adding a help overlay enumerating these bindings.
 * - Avoid locale-sensitive characters if targeting international keyboard layouts (e.g., '{' and '}' may require AltGr on some layouts).
 *
 * PERFORMANCE & SAFETY
 *
 * - Keybinding reset (resetKeybindings) ensures no duplicate registrations across re-initializations (e.g., hot reload).
 * - Scoped data is seeded with { global: project } to let handlers access project-level state uniformly.
 *
 * @param project The active RydProject instance whose state and timeline the bindings will manipulate.
 * @param caretController Controls scroll/caret positioning; injected into transport handlers.
 *
 * @remarks
 * This documentation intentionally omits implementation code and focuses on user-facing & integrator-facing semantics
 * of the registered keybindings.
 */
import { buildKeybindingHandlers } from "../audio/PlaybackController";
import { GridKB } from "../keybindings/grid";
import { NoteKB } from "../keybindings/note";
import { ZoomKB } from "../keybindings/zoom";

import { RydProject } from "../model/project";
import {
    isMetronomeMuted,
    playbackConfStore
} from "../stores/PlaybackConfStore";

import { KeybindingsManager } from "../keybindings/dispatcher";
import { CaretController } from "../utils/ScrollController";
import { PianoRollEditKB } from "../keybindings/PianoRollEdit";
import { workspaceStore } from "../stores/WorkspaceStore";
import { timelineSelectionStore } from "../stores/TimelineSelectionStore";
import { inputPlaneStore } from "../stores/InspectorPanelStore";


export namespace RydKeybindingsInit {
    export function initKeybindings(
        project: RydProject,
        caretController: CaretController,
    ) {
        console.log('INIT: KEYBINDINGS')

        const { increaseZoom, decreaseZoom, resetZoom } = ZoomKB.create();
        const { increaseGridResolution, decreaseGridResolution, resetGridResolution } = GridKB.create();
        const { increaseNoteDuration, decreaseNoteDuration } = NoteKB.create();
        const { octaveUp,
            octaveDown,
            duplicateNotes,
            fillNotes,
            splitNotes,
            deleteNotes,
            nudgeNotesUp,
            nudgeNotesDown,
            nudgeNotesLeft,
            nudgeNotesRight,
            selectAllNotes,
            copyNotes,
            cutNotes,
            pasteNotes,
            currentNoteSelectionDataResolver,
            currentTimeSignatureResolver
        } = PianoRollEditKB.create();

        const keybindingsManager = KeybindingsManager.getInstance();
        keybindingsManager.resetKeybindings();
        keybindingsManager.setScopedData({ global: project });

        const { playPause, record } = buildKeybindingHandlers(project, caretController);
        keybindingsManager.register(playPause);
        keybindingsManager.register(record);

        keybindingsManager.register({
            description: 'Cancel operation',
            scopeUnit: 'global',
            trigger: {
                key: 'Escape',
            },
            handler: () => {
                // cancel the current selection
                timelineSelectionStore.getState().setTimelineSelection(undefined);
                inputPlaneStore.getState().noteSelectionGroups.clear();
            }
        })

        keybindingsManager.register({
            description: 'Toggle Metronome',
            scopeUnit: 'global',
            trigger: {
                key: 'm',
                ctrl: true
            },
            handler: () => playbackConfStore.setState({ isMetronomeMuted: !isMetronomeMuted() })
        });
        keybindingsManager.register({
            description: 'Decrease note duration',
            scopeUnit: 'global',
            trigger: {
                key: '[',
            },
            handler: decreaseNoteDuration
        });

        keybindingsManager.register({
            description: 'Increase note duration',
            scopeUnit: 'global',
            trigger: {
                key: ']',
            },
            handler: increaseNoteDuration
        });
        keybindingsManager.register({
            description: 'Decrease grid resolution',
            scopeUnit: 'global',
            trigger: {
                key: '{',
            },
            handler: decreaseGridResolution
        });

        keybindingsManager.register({
            description: 'Increase grid resolution',
            scopeUnit: 'global',
            trigger: {
                key: '}',
            },
            handler: increaseGridResolution
        });


        keybindingsManager.register({
            description: 'Decrease zoom',
            scopeUnit: 'global',
            trigger: {
                key: '-',
            },
            handler: decreaseZoom
        });

        keybindingsManager.register({
            description: 'Increase zoom',
            scopeUnit: 'global',
            trigger: {
                key: '+',
            },
            handler: increaseZoom
        });

        keybindingsManager.register({
            description: "Move notes octave up",
            scopeUnit: 'selection-active',
            trigger: {
                key: 'ArrowUp',
                shift: true
            },
            handler: octaveUp,
            resolver: currentNoteSelectionDataResolver
        });

        keybindingsManager.register({
            description: "Move notes octave down",
            scopeUnit: 'selection-active',
            trigger: {
                key: 'ArrowDown',
                shift: true
            },
            handler: octaveDown,
            resolver: currentNoteSelectionDataResolver
        });

        keybindingsManager.register({
            description: "Delete notes",
            scopeUnit: 'selection-active',
            trigger: {
                key: 'Delete',
            },
            handler: deleteNotes,
            resolver: currentNoteSelectionDataResolver
        });

        keybindingsManager.register({
            description: "Nudge notes up",
            scopeUnit: 'selection-active',
            trigger: {
                key: 'ArrowUp',
            },
            handler: nudgeNotesUp,
            resolver: currentNoteSelectionDataResolver
        });

        keybindingsManager.register({
            description: "Nudge notes down",
            scopeUnit: 'selection-active',
            trigger: {
                key: 'ArrowDown',
            },
            handler: nudgeNotesDown,
            resolver: currentNoteSelectionDataResolver
        });

        keybindingsManager.register({
            description: "Nudge notes left",
            scopeUnit: 'selection-active',
            trigger: {
                key: 'ArrowLeft',
            },
            handler: nudgeNotesLeft,
            resolver: () => {
                return { c: currentNoteSelectionDataResolver(), ts: currentTimeSignatureResolver() }
            }
        });

        keybindingsManager.register({
            description: "Nudge notes right",
            scopeUnit: 'selection-active',
            trigger: {
                key: 'ArrowRight',
            },
            handler: nudgeNotesRight,
            resolver: () => {
                return { c: currentNoteSelectionDataResolver(), ts: currentTimeSignatureResolver() }
            }
        });

        keybindingsManager.register({
            description: "Duplicate notes",
            scopeUnit: 'selection-active',
            trigger: {
                key: 'd',
            },
            handler: duplicateNotes,
            resolver: () => {
                return { c: currentNoteSelectionDataResolver(), ts: currentTimeSignatureResolver() }
            }
        });

        keybindingsManager.register({
            description: "Toggle between write and select mode",
            scopeUnit: 'global',
            trigger: {
                key: 'w',
            },
            handler: () => {
                const ws = workspaceStore.getState();
                const newMode = ws.uiState.editMode === 'NOTES' ? 'SELECT' : 'NOTES';
                console.log('TOGGLE EDIT MODE', ws.uiState.editMode, '->', newMode);
                workspaceStore.getState().setUIState({ ...ws.uiState, editMode: newMode });
            }
        });

        keybindingsManager.register({
            description: "Fill notes",
            scopeUnit: 'selection-active',
            trigger: {
                key: 'f',
            },
            handler: fillNotes,
            resolver: () => {
                return { c: currentNoteSelectionDataResolver(), ts: currentTimeSignatureResolver() }
            }
        });

        keybindingsManager.register({
            description: "Split notes",
            scopeUnit: 'selection-active',
            trigger: {
                key: 's',
            },
            handler: splitNotes,
            resolver: () => {
                return { c: currentNoteSelectionDataResolver(), ts: currentTimeSignatureResolver() }
            }
        });

        keybindingsManager.register({
            description: "Select all notes",
            scopeUnit: 'track',
            trigger: {
                key: 'a',
                ctrl: true
            },
            handler: selectAllNotes,
            resolver: currentNoteSelectionDataResolver
        });
    }
}
